/**
 * kolLayer2 — Layer 2 AI 复核 for KOL classification
 *
 * 当 Layer 1 启发式判定 'unsure' 时（或 confidence < 0.6），调用 /api/kol-classify
 * 让 AI 做综合判断。结果 7 天缓存。
 *
 * 设计：
 *   - 仅对 unsure 账号触发（节约 token 成本）
 *   - 批量调用（50 个/批，1-2 秒响应）
 *   - localStorage 缓存 pl_kol_layer2_v1，7 天 TTL
 *   - fire-and-forget：不阻塞 Layer 1 渲染
 */

const LS_KEY = 'pl_kol_layer2_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 天

function safeParse(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

function nowMs() { return Date.now(); }

/** 读 cache（自动过滤过期） */
export function loadLayer2Cache() {
  try {
    const saved = safeParse(localStorage.getItem(LS_KEY) || '{}');
    const now = nowMs();
    const valid = {};
    Object.entries(saved).forEach(([username, entry]) => {
      if (entry?.ts && (now - entry.ts) < TTL_MS) {
        valid[username] = entry;
      }
    });
    return valid;
  } catch { return {}; }
}

/** 批量写 cache（自动 sweep 过期条目） */
export function saveLayer2Cache(entries) {
  if (!entries || typeof entries !== 'object') return;
  try {
    const saved = safeParse(localStorage.getItem(LS_KEY) || '{}');
    const now = nowMs();
    // sweep 过期
    Object.keys(saved).forEach(k => {
      if (!saved[k]?.ts || (now - saved[k].ts) >= TTL_MS) delete saved[k];
    });
    // merge new
    Object.entries(entries).forEach(([username, classification]) => {
      if (!username) return;
      saved[username] = { ...classification, ts: now };
    });
    localStorage.setItem(LS_KEY, JSON.stringify(saved));
  } catch {}
}

/**
 * 调 backend 端点做 Layer 2 分类
 * @param {Array} accounts — Layer 1 unsure 的账号列表
 * @param {string} apiBase — proxy base URL（如 'https://primary-lens-production.up.railway.app'）
 * @returns {Promise<{ [username]: { type, confidence, reason } }>}
 */
export async function fetchLayer2Classifications(accounts, apiBase = '') {
  if (!Array.isArray(accounts) || accounts.length === 0) return {};
  const payload = accounts.slice(0, 50).map(a => ({
    username: a.username,
    name: a.name,
    bio: a.bio || '',
    twitterBio: a.twitterBio || '',
    accountType: a.accountType,
    followers: a.followers,
    tags: a.tags || [],
  }));
  try {
    const res = await fetch(`${apiBase}/api/kol-classify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accounts: payload }),
    });
    if (!res.ok) return {};
    const json = await res.json();
    return json?.classifications || {};
  } catch {
    return {};
  }
}

/**
 * 用 Layer 2 结果增强 Layer 1 classification
 * 规则：
 *   - Layer 1 高信心 (>=0.7) → 保留 Layer 1，忽略 Layer 2
 *   - Layer 1 低/中 信心 → 用 Layer 2（如果有）覆盖
 *   - 用户手动 tag → 永远 override（不在此处处理，classifyKolAccount 里已经返回 confidence=1）
 */
export function mergeWithLayer2(layer1Result, layer2Result) {
  if (!layer2Result) return layer1Result;
  if (layer1Result?.confidence >= 0.7) return layer1Result;

  return {
    type: layer2Result.type,
    confidence: layer2Result.confidence,
    signals: { ...(layer1Result?.signals || {}), aiLayer2: true },
    reason: layer2Result.reason || layer1Result?.reason,
    refined: true, // 标记：UI 显示「🤖 AI refined」
  };
}
